/**
 * Card capture (TMMRSL-0015): bearer client-config → Stripe.js Card Element → tokenize. The
 * config is the open tenant's (`tenant` / `platform`) or the reseller's own (`reseller`).
 * The only seam that produces a `cardToken`. Never logs or stores it beyond `onToken`.
 * Submitting is single-flight: a second click while tokenizing or submitting does nothing
 * (ACT-008).
 */
import { useRef, useState, type ReactNode } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCardToken, type CardConfigScope, type TokenizeResult } from './useCardToken';

export interface CardCaptureProps {
  onToken: (token: string) => void | Promise<void | 'retry'>;
  submitLabel: string;
  busy?: boolean;
  children?: ReactNode;
  /** Return false to skip tokenize (e.g. invalid email on Subscribe with card). */
  onBeforeTokenize?: () => boolean;
  /**
   * Required — tenant (subscribe / uncomp) or platform (add-on accept / update card), both inside
   * a tenant workspace; or reseller (the reseller's own card on file, TMMRSL-0046), anywhere.
   */
  scope: CardConfigScope;
}

export function CardCapture({
  onToken,
  submitLabel,
  busy = false,
  children,
  onBeforeTokenize,
  scope,
}: CardCaptureProps) {
  const { t } = useTranslation();
  const { status, complete, cardMessage, tokenize, retry } = useCardToken(scope);
  const [cardError, setCardError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const [working, setWorking] = useState(false);

  async function runToken(result: TokenizeResult): Promise<void | 'retry'> {
    if (result.ok) {
      return onToken(result.token);
    }
    setCardError(result.message);
    return undefined;
  }

  async function handleSubmit() {
    if (busy || inFlight.current) return;
    if (onBeforeTokenize !== undefined && !onBeforeTokenize()) return;
    inFlight.current = true;
    setWorking(true);
    try {
      setCardError(null);
      const first = await tokenize();
      const outcome = await runToken(first);
      if (outcome !== 'retry') return;
      const second = await tokenize();
      await runToken(second);
    } finally {
      inFlight.current = false;
      setWorking(false);
    }
  }

  const shownError = cardError ?? (status === 'ready' ? cardMessage : null);

  return (
    <Stack spacing={2}>
      {status === 'loading' && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('common.loading')}
        </Typography>
      )}
      {status === 'notConfigured' && (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={retry}>
              {t('billing.retry')}
            </Button>
          }
        >
          {t('billing.notConfigured')}
        </Alert>
      )}
      {status === 'unavailable' && (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={retry}>
              {t('billing.retry')}
            </Button>
          }
        >
          {t('billing.unavailable')}
        </Alert>
      )}
      {status === 'unsupported' && <Alert severity="warning">{t('billing.unsupported')}</Alert>}
      {shownError !== null && shownError.length > 0 && (
        <Alert severity="error">{t('billing.cardError', { message: shownError })}</Alert>
      )}
      {(status === 'ready' || status === 'loading') && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('billing.cardLabel')}
        </Typography>
      )}
      <Box
        id="card-container"
        sx={{
          display: status === 'ready' || status === 'loading' ? 'block' : 'none',
          minHeight: status === 'ready' ? 96 : 0,
        }}
      />
      {status === 'ready' && (
        <Typography variant="caption">{t('billing.secureNote')}</Typography>
      )}
      {children}
      {status === 'ready' && (
        <Box>
          <Button
            variant="contained"
            disabled={busy || working || !complete}
            loading={working || busy}
            onClick={() => void handleSubmit()}
          >
            {submitLabel}
          </Button>
        </Box>
      )}
    </Stack>
  );
}
